// src/components/UserSideComponents/packages/PackageConfirmStep.jsx
import {
  ShieldCheck,
  Calendar,
  Clock,
  MapPin,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  BrushIcon,
} from "lucide-react";
import { useState } from "react";
import { convertTo12Hour } from "../../../utils/formatDateTime";
import { userConfirmAction } from "../../../utils/toastUtils";

const PackageConfirmStep = ({ pkg, bookingData, submitting, onConfirm, onBack }) => {
  const [showServices, setShowServices] = useState(false);

  const hasDiscount = pkg.discountedPrice && pkg.discountedPrice < pkg.price;
  const displayPrice = hasDiscount ? pkg.discountedPrice : pkg.price;

  const handleConfirm = async () => {
    const confirmed = await userConfirmAction({
      title: "Confirm Package Booking",
      text: `You'll be booking the "${pkg.name}" package for ₹${displayPrice}. Pay after service completion.`,
      confirmText: "Confirm Booking",
      cancelText: "Cancel",
      icon: "info",
    });
    if (!confirmed) return;
    onConfirm();
  };

  return (
    <div className="p-5 space-y-4">
      {/* TOTAL CARD */}
      <div className="bg-gradient-to-r from-[#ab310c]/5 to-[#EDB5A4]/10 rounded-2xl px-4 py-4 border border-[#ab310c]/10">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500 mb-1">Total Amount</p>
            <p className="text-3xl font-bold text-gray-900">₹{displayPrice}</p>
            {hasDiscount && (
              <p className="text-xs text-green-600 font-medium mt-0.5">
                You save ₹{pkg.price - pkg.discountedPrice}
              </p>
            )}
          </div>
          <div className="text-right">
            <div className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 px-3 py-1.5 rounded-full">
              <ShieldCheck size={14} />
              <span className="text-xs font-semibold">Pay Later</span>
            </div>
            <p className="text-[10px] text-gray-500 mt-1">After service completion</p>
          </div>
        </div>
      </div>

      {/* PACKAGE SUMMARY */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {/* Package name row */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
          {pkg.banner && (
            <img
              src={pkg.banner}
              alt={pkg.name}
              className="w-12 h-12 rounded-xl object-cover flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm truncate">{pkg.name}</p>
            <p className="text-xs text-gray-500">{pkg.category}</p>
          </div>
        </div>

        {/* Services toggle */}
        <button
          onClick={() => setShowServices(!showServices)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition border-b border-gray-100"
        >
          <span>Services ({pkg.services?.length || 0})</span>
          {showServices ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        {showServices && (
          <div className="px-4 py-3 space-y-1.5 bg-gray-50">
            {pkg.services?.map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                <BrushIcon size={12} className="text-[#ab310c] flex-shrink-0" />
                {s}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* VISIT DETAILS */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 pt-3 pb-2">
          Visit Details
        </p>

        {/* Date & Time */}
        {bookingData.slotSnapshot && (
          <div className="flex items-center gap-3 bg-gray-50 rounded-lg mx-4 mb-3 p-3">
            <div className="flex items-center gap-3 pr-3 flex-1">
              <div className="w-9 h-9 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                <Calendar size={16} className="text-[#ab310c]" />
              </div>
              <p className="text-xs font-medium text-gray-900">
                {new Date(bookingData.slotSnapshot.date).toLocaleDateString("en-US", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                })}
              </p>
            </div>
            <div className="h-8 w-px bg-gray-300 flex-shrink-0" />
            <div className="flex items-center gap-3 flex-1">
              <div className="w-9 h-9 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                <Clock size={16} className="text-[#ab310c]" />
              </div>
              <p className="text-xs font-medium text-gray-900">
                {convertTo12Hour(bookingData.slotSnapshot.startTime)} –{" "}
                {convertTo12Hour(bookingData.slotSnapshot.endTime)}
              </p>
            </div>
          </div>
        )}

        {/* Address */}
        {bookingData.location?.address?.formatted && (
          <div className="flex items-start gap-3 bg-gray-50 rounded-lg mx-4 mb-4 p-3">
            <div className="w-9 h-9 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0">
              <MapPin size={16} className="text-[#ab310c]" />
            </div>
            <p className="text-xs text-gray-700 leading-relaxed">
              {bookingData.location.address.formatted}
            </p>
          </div>
        )}
      </div>

      {/* PAYMENT PROCESS */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <CheckCircle2 size={16} className="text-blue-600" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-2">Payment Process</h4>
            <ol className="space-y-1.5 text-xs text-blue-800">
              <li className="flex items-start gap-2">
                <span className="font-semibold min-w-[18px]">1.</span>
                <span>Confirm your booking below</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold min-w-[18px]">2.</span>
                <span>Our professional arrives at your scheduled time</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold min-w-[18px]">3.</span>
                <span>Pay ₹{displayPrice} after service completion (Cash/UPI accepted)</span>
              </li>
            </ol>
          </div>
        </div>
      </div>

      {/* CTAs */}
      <div className="space-y-3 pb-2">
        <button
          onClick={handleConfirm}
          disabled={submitting}
          className="
            w-full py-4 rounded-xl font-semibold text-white
            bg-gradient-to-r from-[#ab310c] to-[#d44a20]
            hover:from-[#d44a20] hover:to-[#ab310c]
            shadow-md shadow-[#ab310c]/25
            hover:shadow-lg hover:shadow-[#ab310c]/35
            transition-all duration-200 active:scale-[0.98]
            disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none
            flex items-center justify-center gap-2
          "
        >
          {submitting ? (
            <>
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Confirming...
            </>
          ) : (
            <>
              Confirm Booking
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </>
          )}
        </button>

        <button
          onClick={onBack}
          disabled={submitting}
          className="w-full border border-gray-200 text-gray-600 font-medium text-sm py-3 rounded-xl hover:bg-gray-50 transition disabled:opacity-50"
        >
          Back to Edit Slot
        </button>

        <p className="text-center text-xs text-gray-400">
          By confirming, you agree to pay ₹{displayPrice} after service completion
        </p>
      </div>
    </div>
  );
};

export default PackageConfirmStep;